/**
 * Anlagen-Section
 * Tabelle mit Anlagen-Nr., Bezeichnung, Hersteller, Typ, Nennleistung, Referenzertrag
 * Hinzufügen / Bearbeiten per Dialog, Löschen mit Bestätigung
 */
import React, { useState, useCallback, useMemo } from "react";
import { Typography } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import Button from "../../../common/inputs/Button";
import Section from "../../../layout/Section";
import BaseTable, {
  TableColumn,
  TableData,
} from "../../../tables/common/BaseTable";
import { formatPowerMW, formatEnergyMWh } from "../../../../utils/formatters";
import { GenerationUnit, GenerationUnitCreate } from "../../../../types";

import GenerationUnitDialog from "./GenerationUnitDialog";

interface GenerationUnitsSectionProps {
  projectId: string;
  units: GenerationUnit[];
  canEdit: boolean;
  onCreate: (items: GenerationUnitCreate[]) => Promise<void>;
  onUpdate: (
    unitId: string,
    data: Partial<GenerationUnitCreate>,
  ) => Promise<void>;
  onDelete: (unitId: string) => Promise<void>;
  selectedUnitId?: string;
}

const TABLE_COLUMNS: TableColumn[] = [
  {
    key: "anlagen_nr",
    label: "Anlagen-Nr.",
    align: "left",
    width: "11%",
    sortable: true,
  },
  {
    key: "bezeichnung",
    label: "Bezeichnung",
    align: "left",
    width: "16%",
    sortable: true,
  },
  {
    key: "hersteller",
    label: "Hersteller",
    align: "left",
    width: "16%",
    sortable: true,
  },
  { key: "typ", label: "Typ", align: "left", width: "17%", sortable: true },
  {
    key: "nennleistung",
    label: "Nennleistung",
    align: "right",
    width: "15%",
    sortable: true,
  },
  {
    key: "referenzertrag",
    label: "Referenzertrag",
    align: "right",
    width: "15%",
    sortable: true,
  },
];

const GenerationUnitsSection: React.FC<GenerationUnitsSectionProps> = ({
  projectId,
  units,
  canEdit,
  onCreate,
  onUpdate,
  onDelete,
  selectedUnitId,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editingUnit, setEditingUnit] = useState<GenerationUnit | null>(null);

  const handleOpenCreate = useCallback(() => {
    setDialogMode("create");
    setEditingUnit(null);
    setDialogOpen(true);
  }, []);

  const handleOpenEdit = useCallback(
    (row: TableData) => {
      const unit = units.find((u) => u.id === row.id);
      if (!unit) return;
      setDialogMode("edit");
      setEditingUnit(unit);
      setDialogOpen(true);
    },
    [units],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogSubmit = useCallback(
    async (formData: GenerationUnitCreate, anzahl?: number) => {
      if (dialogMode === "create") {
        const count = anzahl ?? 1;
        const items = Array.from({ length: count }, () => ({ ...formData }));
        await onCreate(items);
      } else if (editingUnit) {
        await onUpdate(editingUnit.id, formData);
      }
    },
    [dialogMode, editingUnit, onCreate, onUpdate],
  );

  const handleDeleteClick = useCallback(
    async (row: TableData) => {
      const name =
        row.bezeichnung ||
        [row.hersteller, row.typ].filter(Boolean).join(" ") ||
        "diese Anlage";
      if (window.confirm(`Möchten Sie "${name}" wirklich löschen?`)) {
        await onDelete(row.id);
      }
    },
    [onDelete],
  );

  const renderCell = useCallback(
    (column: TableColumn, row: TableData, value: unknown) => {
      switch (column.key) {
        case "nennleistung":
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: "medium",
              }}
            >
              {value != null && typeof value === "number"
                ? `${formatPowerMW(value)} MW`
                : "-"}
            </Typography>
          );
        case "referenzertrag":
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: "medium",
              }}
            >
              {value != null && typeof value === "number"
                ? `${formatEnergyMWh(value)} MWh`
                : "-"}
            </Typography>
          );
        default:
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: "medium",
              }}
            >
              {value != null && value !== "" ? String(value) : "-"}
            </Typography>
          );
      }
    },
    [],
  );

  // Vorschlag für eine neue Anlage. Spiegelt die Vergabe im Backend: gezählt wird über die
  // höchste vergebene Nummer, nicht über die Anzahl der Anlagen – eine gelöschte Nummer bleibt
  // frei, weil die Nummerierung im Genehmigungsantrag mit der Behörde abgestimmt ist.
  // Maßgeblich bleibt das Backend; hier steht sie nur, damit das Feld vorbelegt aufgeht.
  const naechsteAnlagenNr = useMemo(() => {
    // Format A001 wie in ELiA (Formular 1.1, Punkt 2.3). Reine Ziffernfolgen zählen mit:
    // vor der Umstellung vergab das Backend 0001, diese Nummern dürfen nicht doppelt kommen.
    const vergeben = units
      .map((u) => u.anlagen_nr)
      .filter((nr): nr is string => !!nr && /^A?\d+$/.test(nr))
      .map((nr) => Number(nr.replace(/^A/, "")));
    const naechste = (vergeben.length ? Math.max(...vergeben) : 0) + 1;
    return `A${String(naechste).padStart(3, "0")}`;
  }, [units]);

  const tableData: TableData[] = units.map((u) => ({
    id: u.id,
    anlagen_nr: u.anlagen_nr,
    bezeichnung: u.bezeichnung,
    hersteller: u.hersteller,
    typ: u.typ,
    nennleistung: u.nennleistung,
    referenzertrag: u.referenzertrag,
  }));

  return (
    <>
      <Section
        title="Anlagenliste"
        noPadding={true}
        actions={
          canEdit ? (
            <Button
              variant="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
            >
              Neue Anlage
            </Button>
          ) : undefined
        }
      >
        <BaseTable
          columns={TABLE_COLUMNS}
          data={tableData}
          renderCell={renderCell}
          onDelete={canEdit ? handleDeleteClick : undefined}
          actionsColumnWidth="10%"
          actionsColumnAlign="right"
          minWidth={900}
          emptyMessage="Es wurden noch keine Anlagen erfasst"
          showBorder={false}
          onRowClick={canEdit ? handleOpenEdit : undefined}
          selectedRowId={selectedUnitId}
        />
      </Section>

      <GenerationUnitDialog
        projectId={projectId}
        open={dialogOpen}
        mode={dialogMode}
        editingUnit={editingUnit}
        naechsteAnlagenNr={naechsteAnlagenNr}
        onClose={handleCloseDialog}
        onSubmit={handleDialogSubmit}
      />
    </>
  );
};

export default GenerationUnitsSection;
